// Lazy loader for optional runtime feature bundles.
// It keeps debug/performance/map/observed/worker code out of the core bundle.
import { isRuntimeMinifiedBuild, resolveRuntimeAssetUrl } from './assets'
import { hasRuntimeFeature } from './registry'
import type { LuisRuntimeFeatureName } from './types'
import { resolveRuntimeScriptIntegrity } from '@lentystyle/guard/runtime'
import { emitLentyLog } from '@lentystyle/guard/shared'
import { buildRuntimeSecurityViolation, emitRuntimeSecurityViolation } from '../security-events'
import { createTrustedRuntimeScriptUrl } from '../trusted-types'

const FEATURE_DEPENDENCIES: Record<LuisRuntimeFeatureName, LuisRuntimeFeatureName[]> = {
  debug: [],
  performance: [],
  map: ['debug'],
  observed: [],
  worker: [],
  lazy: [],
}

const featureLoadPromises = new Map<LuisRuntimeFeatureName, Promise<boolean>>()

// Ensure all requested runtime features are loaded and registered.
export async function ensureRuntimeFeatures(features: Set<LuisRuntimeFeatureName>): Promise<void> {
  // Skip loading when no feature was requested.
  if (features.size === 0) {
    // Return early when feature set is empty.
    return
  } else {
    // Continue dependency expansion for requested features.
  }

  const expanded = expandFeatureDependencies(features)
  // Load all required feature scripts in parallel.
  await Promise.all(Array.from(expanded).map((feature) => ensureRuntimeFeature(feature)))
}

// Load one runtime feature script if it is not already registered.
async function ensureRuntimeFeature(feature: LuisRuntimeFeatureName): Promise<boolean> {
  // Skip loading when feature already exists in registry.
  if (hasRuntimeFeature(feature)) {
    // Return true when feature is already registered.
    return true
  } else {
    // Continue lazy script load flow.
  }

  const existingLoad = featureLoadPromises.get(feature)
  // Reuse active load promise for duplicate concurrent requests.
  if (existingLoad) {
    // Return existing load promise to avoid duplicate script injection.
    return existingLoad
  } else {
    // Continue with a new script load operation.
  }

  const loadPromise = loadRuntimeFeatureScript(feature)
  featureLoadPromises.set(feature, loadPromise)
  const loaded = await loadPromise
  // Clear failed load cache so future retries are possible.
  if (!loaded) {
    featureLoadPromises.delete(feature)
  } else {
    // Keep resolved load promise cache for loaded feature.
  }
  // Return whether the feature script loaded and registered successfully.
  return loaded
}

// Inject one runtime feature script tag and resolve on load/error.
async function loadRuntimeFeatureScript(feature: LuisRuntimeFeatureName): Promise<boolean> {
  const minSuffix = isRuntimeMinifiedBuild() ? '.min' : ''
  const scriptUrl = resolveRuntimeAssetUrl(`lentystyle.${feature}${minSuffix}.js`)
  // Abort loading when feature script url cannot be resolved.
  if (!scriptUrl) {
    emitRuntimeSecurityViolation(buildRuntimeSecurityViolation(
      'runtime-script-rejected',
      'Runtime feature script URL could not be resolved or was blocked.',
      { feature },
    ))
    emitLentyLog('RUN0009', { feature })
    // Return resolved false when asset url resolution fails.
    return Promise.resolve(false)
  } else {
    // Continue dynamic script injection for resolved feature url.
  }

  const resolvedUrl = new URL(scriptUrl, document.baseURI)
  const integrity = await resolveRuntimeScriptIntegrity(scriptUrl, document.baseURI)
  const isCrossOrigin = resolvedUrl.origin !== window.location.origin
  // Reject cross-origin script loading when no SRI hash can be resolved.
  if (isCrossOrigin && !integrity) {
    emitRuntimeSecurityViolation(buildRuntimeSecurityViolation(
      'runtime-script-rejected',
      'Cross-origin runtime feature script was blocked without integrity metadata.',
      { feature, href: scriptUrl },
    ))
    emitLentyLog('RUN0010', { feature, scriptUrl })
    // Return false so the caller can keep the feature fail-closed.
    return false
  } else {
    // Continue script injection with optional integrity metadata.
  }

  return new Promise<boolean>((resolve) => {
    const script = document.createElement('script')
    // Trusted Types returns a browser URL object while TS still exposes `src` as string.
    script.src = createTrustedRuntimeScriptUrl(scriptUrl) as string
    script.async = true
    script.dataset.luisRuntimeFeature = feature
    // Attach SRI metadata when runtime policy provides an integrity hash.
    if (integrity) {
      script.integrity = integrity
      script.crossOrigin = 'anonymous'
    } else {
      // Keep same-origin scripts without integrity metadata.
    }
    script.onload = () => {
      // Resolve true only when feature registered successfully.
      if (hasRuntimeFeature(feature)) {
        resolve(true)
      } else {
        emitLentyLog('RUN0011', { feature })
        resolve(false)
      }
    }
    script.onerror = () => {
      emitLentyLog('RUN0012', { feature, scriptUrl })
      resolve(false)
    }
    document.head.appendChild(script)
  })
}

// Expand requested features with transitive dependencies.
function expandFeatureDependencies(features: Set<LuisRuntimeFeatureName>): Set<LuisRuntimeFeatureName> {
  const expanded = new Set<LuisRuntimeFeatureName>()
  const queue = Array.from(features)
  // Walk dependency graph until all required features are included.
  while (queue.length > 0) {
    const current = queue.shift() as LuisRuntimeFeatureName
    // Skip already-expanded features to prevent duplicate processing.
    if (expanded.has(current)) {
      continue
    } else {
      // Continue dependency expansion for current feature.
    }
    expanded.add(current)
    const dependencies = FEATURE_DEPENDENCIES[current]
    // Queue direct dependencies for breadth-first expansion.
    for (const dependency of dependencies) {
      // Check whether dependency is already queued for expansion.
      if (!expanded.has(dependency)) {
        queue.push(dependency)
      } else {
        // Skip already expanded dependencies.
      }
    }
  }

  // Return full requested + dependency feature set.
  return expanded
}
